import { createFileRoute, Link } from "@tanstack/react-router";
import { ArrowLeft, Loader2, Mail } from "lucide-react";
import { useId, useState } from "react";
import { PublicFooter, PublicHeader } from "@/components/public-layout";
import { noIndexMeta } from "@/lib/seo";
import { authClient } from "../lib/auth-client";

const AUTH_GRID_CELLS = Array.from(
	{ length: 52 },
	(_, index) => `auth-grid-cell-${index}`,
);

export const Route = createFileRoute("/forgot-password")({
	component: ForgotPasswordPage,
	head: () => ({
		meta: [{ title: "AgentReacher | Reset your password" }, ...noIndexMeta()],
	}),
});

function ForgotPasswordPage() {
	const [email, setEmail] = useState("");
	const [error, setError] = useState<string | null>(null);
	const [loading, setLoading] = useState(false);
	const [emailSent, setEmailSent] = useState(false);
	const emailId = useId();

	const handleSubmit = async (e: React.FormEvent) => {
		e.preventDefault();
		setError(null);
		setLoading(true);

		try {
			const { error: apiError } = await authClient.requestPasswordReset({
				email,
				redirectTo: "/reset-password",
			});
			if (apiError) {
				setError(apiError.message || "Failed to send reset email");
			} else {
				setEmailSent(true);
			}
		} catch (err) {
			setError(
				err instanceof Error ? err.message : "Failed to send reset email",
			);
		} finally {
			setLoading(false);
		}
	};

	return (
		<div className="flex min-h-svh flex-col bg-background text-foreground">
			<PublicHeader />
			<div className="mx-auto flex w-full max-w-6xl flex-1 px-4 sm:px-6">
				<div className="relative flex flex-1 flex-col overflow-hidden bg-border [--auth-row-size:4.75rem] sm:[--auth-row-size:5.25rem]">
					<main className="relative grid flex-1 grid-cols-4 grid-rows-[minmax(var(--auth-row-size),1fr)_repeat(7,minmax(var(--auth-row-size),auto))_minmax(var(--auth-row-size),1fr)] gap-px sm:grid-cols-8">
						{AUTH_GRID_CELLS.map((cellId, index) => (
							<div
								key={cellId}
								aria-hidden="true"
								className={`pointer-events-none rounded-md bg-white dark:bg-black ${index >= 16 ? "hidden sm:block" : ""}`}
							/>
						))}
						<div className="col-span-4 row-start-2 row-end-7 flex flex-col space-y-6 rounded-md bg-white dark:bg-black px-6 py-10 shadow-[0_0_0_1px_var(--border)] sm:col-start-2 sm:col-end-8 sm:px-8 lg:col-start-3 lg:col-end-7">
							{emailSent ? (
								<>
									<div className="space-y-1">
										<h1 className="text-2xl font-semibold tracking-[-0.04em]">
											Check your email
										</h1>
										<p className="text-sm text-muted-foreground">
											We sent a password reset link to{" "}
											<span className="font-medium text-foreground">
												{email}
											</span>
										</p>
									</div>

									<div className="space-y-3">
										<p className="text-sm text-muted-foreground">
											Didn&apos;t receive it?{" "}
											<button
												type="button"
												onClick={() => setEmailSent(false)}
												className="font-medium text-primary transition-colors hover:text-foreground"
											>
												Try again
											</button>
										</p>
										<Link
											to="/login"
											className="flex w-full items-center justify-center gap-2 rounded-md border border-border bg-muted px-4 py-2.5 text-sm font-medium text-foreground transition-colors hover:bg-accent"
										>
											<ArrowLeft className="h-4 w-4" />
											Back to login
										</Link>
									</div>
								</>
							) : (
								<>
									<div className="space-y-1">
										<h1 className="text-2xl font-semibold tracking-[-0.04em]">
											Reset your password
										</h1>
										<p className="text-sm text-muted-foreground">
											Enter your email and we&apos;ll send you a reset link.
										</p>
									</div>

									{error && (
										<div className="rounded-md border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm text-destructive">
											{error}
										</div>
									)}

									<div className="space-y-5">
										<form onSubmit={handleSubmit} className="space-y-4">
											<div>
												<label
													htmlFor={emailId}
													className="mb-2 block text-sm font-medium text-foreground"
												>
													Email
												</label>
												<div className="relative">
													<Mail className="absolute left-4 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
													<input
														id={emailId}
														type="email"
														autoComplete="email"
														value={email}
														onChange={(e) => setEmail(e.target.value)}
														required
														className="w-full rounded-md border border-border bg-muted py-2.5 pl-11 pr-4 text-sm text-foreground placeholder:text-muted-foreground focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/30"
														placeholder="name@company.com"
													/>
												</div>
											</div>

											<button
												type="submit"
												disabled={loading}
												className="flex w-full items-center justify-center gap-2 rounded-md bg-primary px-4 py-2.5 text-sm font-medium text-primary-foreground transition-colors disabled:cursor-not-allowed disabled:opacity-50"
											>
												{loading ? (
													<>
														<Loader2 className="h-4 w-4 animate-spin" />
														Sending...
													</>
												) : (
													"Send reset link"
												)}
											</button>
										</form>

										<p className="text-center text-sm text-muted-foreground">
											Remember your password?{" "}
											<Link
												to="/login"
												className="font-medium text-primary transition-colors hover:text-foreground"
											>
												Log in
											</Link>
										</p>
									</div>
								</>
							)}
						</div>
					</main>
				</div>
			</div>
			<PublicFooter showCta={false} />
		</div>
	);
}
